<script setup lang="ts">
import AuthenticatedLayout from '@/Layouts/AuthenticatedLayout.vue';
import { Head, Link } from '@inertiajs/vue3'
import { library } from "@fortawesome/fontawesome-svg-core"
import { faUserCog } from "@fal"
import { faCheckCircle, faTimesCircle } from "@fas"

import PageHeading from "@/Components/Headings/PageHeading.vue"
import { capitalize } from "@/Composables/capitalize"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { inject, onMounted, ref } from "vue"
import { aikuLocaleStructure } from "@/Composables/useLocaleStructure"
import CountUp from 'vue-countup-v3'
import BackgroundBox from '@/Components/BackgroundBox.vue'
import Icon from '@/Components/Utils/Icon.vue'
import { trans } from 'laravel-vue-i18n'
import Image from '@/Components/Utils/Image.vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
import { useLocaleStore } from '@/Stores/locale'
import { useFormatTime } from '@/Composables/useFormatTime';
import { useFormatCurrency } from '@/Composables/useFormatCurrency';
import Tag from '@/Components/Utils/Tag.vue';
import Chart from 'primevue/chart';

library.add(faCheckCircle, faTimesCircle, faUserCog)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title: string
    stats: {}[]
    data_sales: {}
    data_orders: {}
}>()

console.log('props', props.data_orders)

const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');

    return {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: textColor
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            },
            y: {
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            }
        }
    };
}
const isChartLoaded = ref(false)
const chartOptions = ref(null)
onMounted(() => {
    chartOptions.value = setChartOptions()
})
</script>

<template>

    <Head title="Dashboard" />
    <PageHeading :data="pageHead"></PageHeading>

    <div class="mx-8 mt-6 grid lg:grid-cols-2 gap-x-6 gap-y-8 xl:gap-x-6 ">
        <div class="col-span-2">
            <div class="border-l-4 text-xl border-sky-400 pl-2 pr-4 bg-sky-50 w-fit py-1">
                Grafik penjualan perbulan dari order
                <FontAwesomeIcon v-tooltip="'Termasuk yang lunas dan belum lunas'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
            </div>
            <Chart v-show="isChartLoaded" @loaded="isChartLoaded = true" type="line" :data="data_orders" :options="chartOptions" class="h-[30rem]" />
            <div v-show="!isChartLoaded" class="h-[30rem] skeleton w-full"></div>

        </div>

        <div class="w-full max-w-xl overflow-hidden rounded-xl border border-gray-300">
            <div class="bg-gray-50 flex flex-col justify-center gap-x-4 border-b border-gray-900/5 p-6">
                <div class="flex justify-between">
                    <div class="font-bold">
                        {{ data_sales.name }}
                    </div>
                    <div>
                        <Tag v-tooltip="'Role'" :label="data_sales.role_label" :theme="3" noHoverColor />
                    </div>
                </div>
                <div v-tooltip="'Username'" class="text-sm/6 text-gray-500 w-fit">
                    <FontAwesomeIcon icon='fal fa-user-cog' class='' fixed-width aria-hidden='true' />
                    {{ data_sales.username }}

                </div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Tgl. dibuat</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ useFormatTime(data_sales.created_at) }}
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Total Brands</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ data_sales.brand_count }}
                        <span v-if="data_sales.brand_increase > 0" v-tooltip="'Ditambahkan di bulan ini'" class="text-green-500">(+{{ data_sales.brand_increase }})</span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Total Orders</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ data_sales.order_count }}
                        <span v-if="data_sales.order_increase > 0" v-tooltip="'Ditambahkan di bulan ini'" class="text-green-500">(+{{ data_sales.order_increase }})</span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Total Invoices</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ data_sales.invoice_count }}
                        <span v-if="data_sales.invoice_increase > 0" v-tooltip="'Ditambahkan di bulan ini'" class="text-green-500">(+{{ data_sales.invoice_increase }})</span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Total Customer</dt>
                    <dd class="flex items-start gap-x-2 font-medium">
                        {{ data_sales.customer_count }}
                        <span v-if="data_sales.customer_increase > 0" v-tooltip="'Ditambahkan di bulan ini'" class="text-green-500">(+{{ data_sales.customer_increase }})</span>
                    </dd>
                </div>

            </dl>
        </div>

        <div class="w-full mx-auto max-w-4xl grid grid-cols-1 gap-x-2 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-1">
            <div class="first:bg-sky-50 px-4 py-6 sm:px-6 lg:px-8 rounded border border-gray-200">
                <p class="text-sm/6 font-medium text-gray-400">{{ stats.total_sale?.name }}</p>
                <p class="mt-2 flex items-baseline gap-x-2">
                    <span class="text-4xl font-semibold tracking-tight">
                        <CountUp :endVal='stats.total_sale?.value' :duration='1.5' :scrollSpyOnce='true' :options='{
                            formattingFn: (value: number) => useLocaleStore().currencyFormat(stats.total_sale?.value)
                        }' />
                    </span>
                    <!-- <span v-if="stats.total_sale?.unit" class="text-sm text-gray-400">{{ stats.total_sale?.unit }}</span> -->
                </p>
            </div>

            <div class="px-4 py-6 sm:px-6 lg:px-8 rounded border border-gray-200">
                <p class="text-sm/6 font-medium text-gray-400">{{ stats.orders_can_picked?.name }}</p>
                <p class="mt-2 flex items-baseline gap-x-2">
                    <Link :href="route('sales.manufactures.can_picked')" class="primaryLink text-4xl font-semibold tracking-tight">
                        <CountUp :endVal='stats.orders_can_picked?.value' :duration='1.5' :scrollSpyOnce='true' :options='{
                            formattingFn: (value: number) => useLocaleStore().number(stats.orders_can_picked?.value)
                        }' />
                    </Link>
                    <!-- <span v-if="stats.total_sale?.unit" class="text-sm text-gray-400">{{ stats.total_sale?.unit }}</span> -->
                </p>
            </div>
        </div>
    </div>



    <div>


    </div>
</template>
